/**
 * Citation Utilities
 * Domain normalization and citation aggregation for consensus analysis
 */

interface ClaimCitation {
  text: string;
  type: 'url' | 'reference' | 'doi';
  normalizedDomain?: string;
  modelKey?: string; // Which model provided this citation
}

interface CitationCount {
  domain: string;
  count: number;
  modelKeys: string[];
}

/**
 * Validate URL is well-formed and safe
 * Filters out invalid/malformed URLs
 */
export function isValidUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    // Only allow http/https protocols
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Normalize URL to domain for comparison
 * Removes www, protocol, path, query params
 */
export function normalizeDomain(url: string): string | null {
  if (!isValidUrl(url)) return null;

  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * Truncate long URLs for display
 * Keeps domain and shortens path
 */
export function truncateUrl(url: string, maxLength: number = 60): string {
  if (url.length <= maxLength) return url;

  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname;
    const path = urlObj.pathname + urlObj.search;

    if (domain.length >= maxLength - 3) {
      return domain.substring(0, maxLength - 3) + '...';
    }

    const remainingLength = maxLength - domain.length - 6; // 6 for "..." and padding
    if (path.length > remainingLength) {
      return domain + path.substring(0, remainingLength) + '...';
    }

    return url;
  } catch {
    // Fallback for non-URLs
    return url.substring(0, maxLength - 3) + '...';
  }
}

/**
 * Aggregate citations by domain to find shared sources
 * Returns domains cited by 2+ models with counts
 */
export function aggregateCitationsByDomain(
  citations: ClaimCitation[]
): CitationCount[] {
  const domainMap = new Map<string, Set<string>>();

  // Group by normalized domain
  for (const citation of citations) {
    if (citation.type !== 'url') continue;

    const domain = citation.normalizedDomain || normalizeDomain(citation.text);
    if (!domain) continue;

    if (!domainMap.has(domain)) {
      domainMap.set(domain, new Set());
    }

    if (citation.modelKey) {
      domainMap.get(domain)!.add(citation.modelKey);
    }
  }

  // Convert to array and filter for shared citations (2+ models)
  const aggregated: CitationCount[] = [];
  domainMap.forEach((modelKeys, domain) => {
    if (modelKeys.size >= 2) {
      aggregated.push({
        domain,
        count: modelKeys.size,
        modelKeys: Array.from(modelKeys)
      });
    }
  });

  // Sort by count descending
  return aggregated.sort((a, b) => b.count - a.count);
}

/**
 * Check if a specific citation is shared across models
 */
export function isSharedCitation(
  citation: ClaimCitation,
  allCitations: ClaimCitation[]
): { isShared: boolean; count: number; modelKeys: string[] } {
  if (citation.type !== 'url') {
    return { isShared: false, count: 1, modelKeys: [] };
  }

  const domain = citation.normalizedDomain || normalizeDomain(citation.text);
  if (!domain) {
    return { isShared: false, count: 1, modelKeys: [] };
  }

  const modelKeys = new Set<string>();
  for (const c of allCitations) {
    if (c.type !== 'url') continue;
    const cDomain = c.normalizedDomain || normalizeDomain(c.text);
    if (cDomain === domain && c.modelKey) {
      modelKeys.add(c.modelKey);
    }
  }

  return {
    isShared: modelKeys.size >= 2,
    count: modelKeys.size,
    modelKeys: Array.from(modelKeys)
  };
}

export interface DedupedCitation extends ClaimCitation {
  citedByModels: string[];
  citationCount: number;
}

export interface CategorizedCitations {
  sharedSources: DedupedCitation[];
  uniqueSources: DedupedCitation[];
}

/**
 * Deduplicate citations by domain and categorize into shared vs unique
 * Shared = cited by 2+ models, Unique = cited by only 1 model
 */
export function deduplicateAndCategorizeCitations(
  citations: ClaimCitation[]
): CategorizedCitations {
  // Filter to only valid URLs
  const validCitations = citations.filter(c => c.type === 'url' && isValidUrl(c.text));

  // Group by normalized domain
  const domainMap = new Map<string, {
    citation: ClaimCitation;
    modelKeys: Set<string>;
  }>();

  for (const citation of validCitations) {
    const domain = citation.normalizedDomain || normalizeDomain(citation.text);
    if (!domain) continue;

    if (!domainMap.has(domain)) {
      domainMap.set(domain, {
        citation: citation,
        modelKeys: new Set()
      });
    }

    if (citation.modelKey) {
      domainMap.get(domain)!.modelKeys.add(citation.modelKey);
    }
  }

  // Convert to deduped citations and categorize
  const sharedSources: DedupedCitation[] = [];
  const uniqueSources: DedupedCitation[] = [];

  domainMap.forEach(({ citation, modelKeys }) => {
    const dedupedCitation: DedupedCitation = {
      ...citation,
      citedByModels: Array.from(modelKeys),
      citationCount: modelKeys.size
    };

    if (modelKeys.size >= 2) {
      sharedSources.push(dedupedCitation);
    } else {
      uniqueSources.push(dedupedCitation);
    }
  });

  // Sort by citation count (descending) for shared, alphabetically for unique
  sharedSources.sort((a, b) => b.citationCount - a.citationCount);
  uniqueSources.sort((a, b) => {
    const domainA = a.normalizedDomain || a.text;
    const domainB = b.normalizedDomain || b.text;
    return domainA.localeCompare(domainB);
  });

  return { sharedSources, uniqueSources };
}
